import { RefObject, useEffect, useState } from 'react';
import { asyncScheduler, fromEvent, merge, Observable } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

const THROTTLE_TIME_MS = 300;

interface ChildNodeCustom {
  id: string;
  offsetTop: number;
  clientHeight: number;
}

export function useItemInViewport(
  containerRef: RefObject<HTMLElement> | null,
  idPrefix: string,
  minVisibilityPercent = 1 // a number between 0 and 1 which specifies how much of the item's height to be visible
): Set<string> {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set([]));
  useEffect(() => {
    if (!containerRef?.current) {
      return undefined;
    }

    const onScrollCallback = (): void => {
      if (!containerRef?.current) {
        return;
      }
      const scrollTop = containerRef.current.scrollTop;
      const viewportHeight = containerRef.current.clientHeight;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const childNodes: ChildNodeCustom[] = [...containerRef.current.childNodes.values()];
      setVisibleItems(
        new Set(
          childNodes
            .filter((cn) => cn.id.startsWith(idPrefix))
            .filter((cn) => {
              // check top bound // check bottom bound
              const viewportIntersection = getIntersection(
                cn.offsetTop,
                cn.offsetTop + cn.clientHeight,
                scrollTop,
                scrollTop + viewportHeight
              );
              if (viewportIntersection == null) {
                return false;
              }
              const visibilityPercent = (viewportIntersection.end - viewportIntersection.start) / cn.clientHeight;
              if (visibilityPercent < minVisibilityPercent) {
                return false;
              }
              return true;
            })
            .map((cn) => cn.id.split(idPrefix)[1])
        )
      );
    };

    const subscription = merge(
      fromEvent(containerRef.current, 'scroll'),
      fromEvent(containerRef.current, 'resize'),
      fromEvent(window, 'resize'),
      // get events when children of containerRef.current change, so we rerun the visibility listener
      observeOnMutation(containerRef.current, { attributes: true, childList: true, characterData: true })
    )
      .pipe(throttleTime(THROTTLE_TIME_MS, asyncScheduler, { trailing: true }))
      .subscribe(onScrollCallback);

    // Check once for current position
    onScrollCallback();

    return (): void => {
      subscription.unsubscribe();
    };
  }, [containerRef, idPrefix, minVisibilityPercent]);

  return visibleItems;
}

const observeOnMutation = (target: Node, config: MutationObserverInit): Observable<MutationRecord[]> => {
  return new Observable((observer) => {
    const mutation = new MutationObserver((mutations, instance) => {
      observer.next(mutations);
    });
    mutation.observe(target, config);
    return (): void => {
      mutation.disconnect();
    };
  });
};

export const getIntersection = (
  startA: number,
  endA: number,
  startB: number,
  endB: number
): null | { start: number; end: number } => {
  if (startB > endA || startA > endB) {
    return null;
  } else {
    return { start: Math.max(startA, startB), end: Math.min(endA, endB) };
  }
};
